import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  NavLink,
  Link as RouterLink,
  useHistory,
  useLocation,
} from "react-router-dom";
import { MOBILE_BREAKPOINT } from "../../config";
import {
  BillingStatus,
  useBrandBillingQuery,
  useBrandNotificationCountQuery,
  useGetBrandSocialsQuery,
} from "../../graphql/generated";
import { useDemoMode } from "../../hooks/useDemoMode";
import useGqlClient from "../../hooks/useGqlClient";
import { useTheme } from "../../hooks/useTheme";
import { useWindowSize } from "../../hooks/useWindowSize";
import RocketEmoji from "../../images/rocket-emoji.png";
import { BriefTabs } from "../../routes/brands/briefs";
import { authSelectors } from "../../store/auth/selector";
import styled, { ThemeProvider, css } from "../../styles";
import { darkTheme } from "../../styles/theme";
import withMargin from "../../styles/withMargin";
import withPadding from "../../styles/withPadding";
import { BookingTabs } from "../../utils/enums";
import { AccountSwitcher } from "../AccountSwitcher";
import { Button } from "../CTA";
import { CardDivider } from "../Divider";
import { Flex } from "../Flex";
import { Logo } from "../Logo";
import { LogoMark } from "../LogoMark";
import MobileMenu from "../MobileMenu";
import { Steps } from "../ProgressBar";
import { Text } from "../Text";
import { UpgradePlan } from "../UpgradePlan";
import { View } from "../View";
import { BookingsIcon } from "../icons/BookingsIcon";
import { ContentIcon } from "../icons/ContentIcon";
import { ListingsIcon } from "../icons/ListingsIcon";
import { LocationsIcon } from "../icons/LocationsIcon";
import { ReportsIcon } from "../icons/ReportsIcon";
import { RequestsIcon } from "../icons/RequestsIcon";
import { SettingsIcon } from "../icons/SettingsIcon";
import { WarningIcon } from "../icons/WarningIcon";
import SetUpGuideModal from "./SetupGuide";
import { UserSettingsSwitcher } from "./UserSettingsSwitcher";

interface MobileNavProps {
  menuToggle: () => void;
  showMenu: boolean;
}

interface BrandNavProps extends MobileNavProps {
  settingsToggle: (s?: boolean) => void;
  showSettings: boolean;
}

const Nav = styled.div<{
  showMenu: boolean;
  colorScheme: "light" | "dark";
}>`
  display: flex;
  height: 100vh;
  z-index: 3;
  user-select: none;
  width: 72px;

  flex-direction: column;
  border-radius: 0px;

  transition: width 300ms cubic-bezier(0.4, 0, 0.2, 1);

  .nav-link-label,
  .nav-chevron,
  .account-switcher {
    @media (min-width: ${MOBILE_BREAKPOINT}px) {
      visibility: hidden;
      opacity: 0;
      transition: opacity 100ms ease-in-out 50ms, visibility 0s linear 0ms;
    }
  }

  .account-switcher-indicator {
    right: 4px;
  }

  .plan-link-card-animation {
    visibility: hidden;
    opacity: 0;
    max-height: 0px;
    transition: max-height 200ms ease-out 0ms, opacity 0ms ease-in-out 0ms,
      visibility 0s ease-in-out 0ms;
  }

  .show-on-nav-hover {
    visibility: hidden;
    opacity: 0;
    transition: opacity 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      visibility 0s linear 400ms;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${(p) => p.theme.spacing.s};
  }

  .reconnect-alert-icon {
    transition: opacity 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      visibility 0s linear 0ms;
    visibility: visible;
    opacity: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .setup-guide-link-card {
    padding: ${(p) => p.theme.spacing.s};
    min-height: 52px;
    transition: min-height 150ms ease-in-out 100ms,
      padding 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .setup-guide-progress-icon {
    transition: opacity 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      visibility 0s linear 0ms;
    visibility: visible;
    opacity: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .setup-guide-progress-text {
    width: 100%;
    text-align: center;
    transition: width 150ms ease-in-out, white-space 150ms ease-in-out;
  }

  .reconnect-socials-card {
    padding: ${(p) => p.theme.spacing.s};
    height: 42px;
    transition: height 150ms ease-in-out 100ms,
      padding 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .reconnect-socials-text {
    width: 100%;
    text-align: center;
    transition: width 150ms ease-in-out, white-space 150ms ease-in-out;
  }

  .nav-link-label {
    margin-left: 0;
  }

  .notification,
  .nav-upgrade-prompt {
    @media (min-width: ${MOBILE_BREAKPOINT}px) {
      visibility: hidden;
      opacity: 0;
      transition: opacity 150ms ease-in-out 50ms, visibility 0s linear 0ms;
    }
  }

  .logo-mark,
  .notification-indicator {
    display: block;
    opacity: 1;
    transition: opacity 150ms ease-in-out;
  }

  .word-mark {
    display: none;
    opacity: 0;
    transition: opacity 150ms ease-in-out;
  }

  &:hover {
    @media (min-width: ${MOBILE_BREAKPOINT}px) {
      width: 238px;

      .logo-mark,
      .notification-indicator {
        display: none;
        opacity: 0;
      }
      .word-mark {
        display: block;
        opacity: 1;
      }
    }

    .account-switcher-indicator {
      right: 36px;
    }

    .setup-guide-link-card {
      padding: ${(p) => p.theme.spacing.s};
      min-height: 98px;
    }

    .setup-guide-progress-icon {
      opacity: 0;
      visibility: hidden;
      transition: opacity 100ms cubic-bezier(0.4, 0, 1, 1) 0ms,
        visibility 0s linear 100ms;
    }

    .setup-guide-progress-text {
      width: 80px;
      white-space: nowrap;
      animation: none;
    }

    .reconnect-socials-card {
      padding: ${(p) => p.theme.spacing.s};
      height: 98px;
    }

    .reconnect-socials-text {
      width: 80px;
      white-space: nowrap;
      animation: none;
      transition: width 200ms cubic-bezier(0.16, 1, 0.3, 1) 200ms;
    }

    .reconnect-alert-icon {
      opacity: 0;
      visibility: hidden;
      transition: opacity 100ms cubic-bezier(0.4, 0, 1, 1) 0ms,
        visibility 0s linear 100ms;
    }

    .show-on-nav-hover {
      visibility: visible;
      opacity: 1;
      transition: opacity 400ms cubic-bezier(0.16, 1, 0.3, 1) 100ms,
        visibility 0s linear 0ms;
    }

    .plan-link-card-animation {
      transition: max-height 200ms ease-in-out 100ms,
        opacity 200ms ease-in-out 100ms, visibility 0s linear 0ms;
      visibility: visible;
      max-height: 60px;
      opacity: 1;
      width: 100%;
      overflow: hidden;
    }

    .nav-link-label {
      margin-left: ${(p) => p.theme.spacing.m};
    }

    .nav-link-label,
    .notification,
    .nav-upgrade-prompt,
    .nav-chevron,
    .account-switcher {
      max-height: 20px;
      visibility: visible;
      opacity: 1;
    }

    .notification,
    .nav-upgrade-prompt {
      transition: opacity 150ms ease-in-out 100ms, visibility 0s linear 100ms;
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: absolute;
    left: 0;
    border-radius: ${(p) => p.theme.misc.borderRadius};
    margin: ${(p) => p.theme.spacing.m};
    height: auto;
    top: 42px;
    bottom: auto;
    width: 100%;
    max-width: 300px;
    box-sizing: border-box;
    border-right: none;
    transition: display 0s, opacity 150ms ease-in-out;
    box-shadow: ${(p) => p.theme.shadow.cardHeavy};
    background: ${(p) => p.theme.color.card.background};
    ${(p) =>
      p.showMenu
        ? css`
            display: flex;
            opacity: 1;
          `
        : css`
            display: none;
            opacity: 0;
          `}

    .nav-link-label,
    .notification,
    .nav-chevron {
      visibility: visible;
      opacity: 1;
    }

    .nav-link-label {
      margin-left: ${(p) => p.theme.spacing.m};
    }

    .notification {
      transition: opacity 150ms ease-in-out 100ms, visibility 0s linear 100ms;
    }

    .logo-mark,
    .notification-indicator {
      display: none;
      opacity: 0;
    }
    .word-mark {
      display: none;
      opacity: 0;
    }
  }
`;

const BottomSection = styled.div`
  margin-top: auto;
`;

const Link = styled(NavLink).attrs({
  activeClassName: "isActive",
})`
  display: flex;
  align-items: center;
  font-size: ${(p) => p.theme.typography.size.s};
  font-weight: ${(p) => p.theme.typography.weight.semi};
  color: ${(p) => p.theme.color.typography.text};
  text-decoration: none;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  padding: 9px ${(p) => p.theme.spacing.l};
  margin: ${(p) => p.theme.spacing.xs} 0;
  line-height: 130%;
  min-height: 23px;

  white-space: nowrap;
  overflow: hidden;

  span {
    margin-top: 1px;
    &.notification {
      color: #fff;
      background: ${(p) => p.theme.color.primary};
    }
  }

  &:hover,
  &.isActive {
    color: ${(p) => p.theme.color.primary};
    background-color: ${(p) => p.theme.color.input.hover};

    .fill-hover {
      path {
        fill: ${(p) => p.theme.color.primary};
      }
    }

    .stroke-hover {
      path {
        stroke: ${(p) => p.theme.color.primary};
      }
    }
  }

  ${withMargin}
`;

const IconWrap = styled.div<{ nestedLink?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) =>
    p.nestedLink
      ? css`
          margin-left: ${(p) => p.theme.spacing.s};
        `
      : null}
`;

export const Notification = styled.span<{ isActive: boolean }>`
  height: ${(p) => p.theme.spacing.m};
  width: ${(p) => p.theme.spacing.m};
  line-height: 1;
  padding: 3px;
  border-radius: 100%;
  margin-left: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(p) => p.theme.typography.size.xxs};
  border-radius: 100%;
  margin-left: auto;
  color: ${(p) => p.theme.color.secondary};
  font-weight: ${(p) => p.theme.typography.weight.bold};
  transition: opacity 150ms ease-in-out;
  opacity: ${(p) => (p.isActive ? 1 : 0)};
  opacity: 0;
`;

const NotificationIndicator = styled.span`
  position: absolute;
  height: ${(p) => p.theme.spacing.xs};
  width: ${(p) => p.theme.spacing.xs};
  border-radius: 100%;
  background-color: ${(p) => p.theme.color.primary};
  right: -${(p) => p.theme.spacing.m};
`;

const StyledCardDivider = styled(CardDivider)`
  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const SetUpGuideStepsWrap = styled(View)`
  margin: ${(p) => p.theme.spacing.s};
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.color.constructive}10 !important;
  gap: 0px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: ${(p) => p.theme.misc.borderRadius};
  border: 1px solid ${(p) => p.theme.color.constructive};
  overflow: hidden;
  position: relative;

  &:hover {
    cursor: pointer;
  }

  ${(p) =>
    p.onClick
      ? css`
          cursor: pointer;
        `
      : ``};

  &.isActive {
    display: none;
  }

  ${withPadding};
  ${withMargin};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ReconnectSocialsWrap = styled(SetUpGuideStepsWrap)`
  background: ${(p) => p.theme.color.warning}10 !important;
  border: 1px solid ${(p) => p.theme.color.warning};
  position: relative;
`;

const Wrap = styled.header`
  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-content: center;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: ${(p) => p.theme.spacing.l};
  padding-top: ${(p) => p.theme.spacing.l};
  padding-left: ${(p) => p.theme.spacing.l};
  padding-right: ${(p) => p.theme.spacing.l};

  @media (min-width: 1200px) {
    padding-left: ${(p) => p.theme.spacing.xl};
    padding-right: ${(p) => p.theme.spacing.xl};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-bottom: ${(p) => p.theme.spacing.m};
  }

  grid-template-columns: 1fr 1fr;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopSwitcher = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

export interface MatchParams {
  page: string;
}

const MobileClickHandler = styled.div<{
  showMenu: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  ${(p) =>
    p.showMenu
      ? css`
          visibility: visible;
          opacity: 1;
        `
      : css`
          visibility: hidden;
          opacity: 0;
        `}

  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const MobileHeader = (props: MobileNavProps) => {
  return (
    <Wrap>
      <Left>
        <Flex align="center">
          <MobileMenu menuToggle={props.menuToggle} showMenu={props.showMenu} />
          <View margin="0 0 0 s">
            <SetUpGuideSteps />
          </View>
        </Flex>
      </Left>
      <Right>
        <AccountSwitcher />
      </Right>
    </Wrap>
  );
};

export const BrandNavigation = () => {
  const window = useWindowSize();
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);

  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  const excludedPaths = [
    "/b/bookings/" + BookingTabs.New + "/book",
    "/b/bookings/" + BookingTabs.Upcoming + "/book",
    "/b/bookings/" + BookingTabs.InProgress + "/book",
    "/b/bookings/" + BookingTabs.Completed + "/book",
    "/b/bookings/" + BookingTabs.Archived + "/book",
    "/b/listings/lst",
    "/b/briefs/" + BriefTabs.Draft + "/cont_brf_",
    "/b/briefs/" + BriefTabs.Matching + "/cont_brf_",
    "/b/briefs/" + BriefTabs.InProgress + "/cont_brf_",
    "/b/briefs/" + BriefTabs.Review + "/cont_brf_",
    "/b/briefs/" + BriefTabs.Complete + "/cont_brf_",
  ];

  const isExcludedPath = excludedPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  return (
    <>
      {window.width !== undefined &&
      window.width < MOBILE_BREAKPOINT &&
      !isExcludedPath ? (
        <MobileHeader
          showMenu={showMenu}
          menuToggle={() => setShowMenu((s) => !s)}
        />
      ) : null}
      <BrandNavigationMenu
        menuToggle={() => setShowMenu((s) => !s)}
        showMenu={showMenu}
        showSettings={showSettingsDropdown}
        settingsToggle={() => setShowSettingsDropdown((s) => !s)}
      />
    </>
  );
};

export const BrandNavigationMenu = (props: BrandNavProps) => {
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const activeGroupId = useSelector(authSelectors.activeGroupId);
  const client = useGqlClient();
  const theme = useTheme();
  const [showUpgradeOverlay, setShowUpgradeOverlay] = useState(false);

  let enableUgc = useFeatureFlagEnabled("ugc");

  const window = useWindowSize();
  const isDesktop = window.width && window.width > MOBILE_BREAKPOINT;

  const { data: notificationCountData } = useBrandNotificationCountQuery(
    client,
    {
      brandID: activeBrandId ? activeBrandId : "",
    },
    {
      enabled: activeBrandId !== "",
      // refetchOnWindowFocus: false,
    }
  );

  const { isDemoMode } = useDemoMode();

  const { data } = useBrandBillingQuery(
    client,
    {
      brandID: activeBrandId ? activeBrandId : "",
    },
    {
      enabled: activeBrandId !== "",
      // refetchOnWindowFocus: false,
    }
  );

  const notificationCountBriefs =
    notificationCountData?.brand?.unreadMessageCountBriefs ?? 0;
  let notificationCountBookings = 0;

  if (isDemoMode) {
    notificationCountBookings = 4;
  } else if (notificationCountData?.brand) {
    notificationCountBookings =
      notificationCountData.brand.numApplicationsToReview +
      notificationCountData.brand.numBookingsToAcknowledge +
      notificationCountData.brand.numBookingsToReview +
      notificationCountData.brand.numBookingPostSubmissionsToReview +
      notificationCountData.brand.unreadMessageCountBookings;
  }

  return (
    <ThemeProvider theme={isDesktop ? darkTheme : theme}>
      {showUpgradeOverlay ? (
        <UpgradePlan onCancel={() => setShowUpgradeOverlay(false)} />
      ) : null}
      <Nav
        colorScheme={theme.name as "light" | "dark"}
        showMenu={props.showMenu}
        onMouseLeave={() => {
          if (props.showSettings) {
            props.settingsToggle(false);
          }
        }}
      >
        <Flex
          justify="center"
          align="flex-start"
          className="word-mark"
          margin="xl l s"
          style={{ marginLeft: 18 }}
        >
          <Logo />
        </Flex>
        <Flex
          justify="center"
          align="center"
          className="logo-mark"
          margin="xl l s"
          style={{ marginLeft: 18 }}
        >
          <LogoMark />
        </Flex>
        <DesktopSwitcher>
          <View padding="s s">
            <AccountSwitcher />
          </View>
        </DesktopSwitcher>

        <Flex direction="column" margin="m s 0">
          {enableUgc ? (
            <LinkSectionText className="nav-link-label" margin="s 0 xs s">
              Influencer Marketing
            </LinkSectionText>
          ) : null}
          {
            <Link margin="0 0 xs 0" to="/b/reports">
              <IconWrap>
                <ReportsIcon colorPreset="text" className="stroke-hover" />
              </IconWrap>
              <span className="nav-link-label">Reports</span>
            </Link>
          }

          {
            <Link margin="0 0 xs 0" to="/b/content">
              <IconWrap>
                <ContentIcon colorPreset="text" className="stroke-hover" />
              </IconWrap>
              <span className="nav-link-label">Content</span>
            </Link>
          }
          {activeBrandId && (
            <Link
              isActive={(_match, location) => {
                return (
                  location.pathname.includes("/b/listings") ||
                  location.pathname.includes("/b/create-listing")
                );
              }}
              margin="0 0 xs 0"
              to="/b/listings"
            >
              <IconWrap>
                <ListingsIcon colorPreset="text" className="fill-hover" />
              </IconWrap>
              <span className="nav-link-label">Campaigns</span>
            </Link>
          )}

          {activeBrandId && (
            <Link margin="0 0 xs 0" to="/b/bookings">
              <Flex align="center" style={{ position: "relative" }}>
                <IconWrap>
                  <BookingsIcon colorPreset="text" className="stroke-hover" />
                </IconWrap>
                {notificationCountBookings > 0 ? (
                  <NotificationIndicator className="notification-indicator" />
                ) : null}
              </Flex>
              <>
                <span className="nav-link-label">Bookings</span>
              </>
              {notificationCountBookings > 0 ? (
                <>
                  <div style={{ marginLeft: "auto" }}>
                    <Notification isActive={true} className="notification">
                      {notificationCountBookings}
                    </Notification>
                  </div>
                </>
              ) : null}
            </Link>
          )}
          {!enableUgc && activeBrandId ? (
            <Link to="/b/locations">
              <IconWrap>
                <LocationsIcon colorPreset="text" className="stroke-hover" />
              </IconWrap>
              <span className="nav-link-label">Locations</span>
            </Link>
          ) : null}
          {enableUgc && activeBrandId ? (
            <>
              <Flex align="center" margin="xl 0 xs 0">
                <LinkSectionText className="nav-link-label" margin="0 0 0 s">
                  Ad Creatives{" "}
                </LinkSectionText>
                <BetaTag className="nav-link-label" style={{ marginTop: -3 }}>
                  NEW
                </BetaTag>
              </Flex>
              <Link margin="0 0 xs 0" to="/b/briefs">
                <Flex align="center" style={{ position: "relative" }}>
                  <IconWrap>
                    <RequestsIcon colorPreset="text" className="fill-hover" />
                  </IconWrap>
                  {notificationCountBriefs > 0 ? (
                    <NotificationIndicator className="notification-indicator" />
                  ) : null}
                </Flex>
                <>
                  <span className="nav-link-label">Briefs</span>
                </>

                {notificationCountBriefs > 0 ? (
                  <>
                    <div style={{ marginLeft: "auto" }}>
                      <Notification isActive={true} className="notification">
                        {notificationCountBriefs}
                      </Notification>
                    </div>
                  </>
                ) : null}
              </Link>
            </>
          ) : null}
          {!enableUgc && activeBrandId ? (
            <Link to="/b/settings" margin="0 0 xs 0">
              <IconWrap>
                <SettingsIcon colorPreset="text" className="stroke-hover" />
              </IconWrap>
              <span className="nav-link-label">Settings</span>
            </Link>
          ) : null}
          {activeGroupId && (
            <Link to="/b/group-settings" margin="0 0 xs 0">
              <IconWrap>
                <SettingsIcon colorPreset="text" className="stroke-hover" />
              </IconWrap>
              <span className="nav-link-label">Settings</span>
            </Link>
          )}
        </Flex>

        <StyledCardDivider margin="s 0" />

        <BottomSection>
          <DesktopOnly>
            {data &&
            data.brand &&
            (data.brand.billingStatus === BillingStatus.BillingStatusInTrial ||
              data.brand.billingStatus ===
                BillingStatus.BillingStatusFreePlanExpired) ? (
              <SetUpGuideSteps />
            ) : (
              <ReconnectSocials />
            )}
          </DesktopOnly>
          <Flex direction="column" margin="0 s m">
            <UserSettingsSwitcher />
          </Flex>
        </BottomSection>
      </Nav>
      <MobileClickHandler
        showMenu={props.showMenu}
        onClick={() => {
          props.menuToggle();
          if (props.showSettings) {
            props.settingsToggle(false);
          }
        }}
      />
    </ThemeProvider>
  );
};

const DesktopOnly = styled.div`
  margin-top: ${(p) => p.theme.spacing.s};
  display: block;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const SetUpGuideSteps = () => {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const theme = useTheme();
  const window = useWindowSize();
  const client = useGqlClient();
  const activeBrandId = useSelector(authSelectors.activeBrandId);

  const { data } = useBrandBillingQuery(
    client,
    {
      brandID: activeBrandId ? activeBrandId : "",
    },
    {
      enabled: activeBrandId !== "",
      // refetchOnWindowFocus: false,
    }
  );

  const steps = useMemo(() => {
    const hasLocations =
      data && data.brand && data.brand.numLocations > 0 ? true : false;
    const hasListings =
      data && data.brand && data.brand.numListings > 0 ? true : false;
    const hasSubscription = data && data.brand && data.brand.stripeCustomerId;
    const stepsCompleted = [hasLocations, hasListings, hasSubscription].filter(
      Boolean
    ).length;
    const nextStep = !hasLocations
      ? "Add your locations"
      : !hasListings
      ? "Create your first campaign"
      : "Go live";

    return {
      hasLocations,
      hasListings,
      hasSubscription,
      stepsCompleted,
      nextStep,
    };
  }, [data]);

  if (!data || !data.brand) {
    return null;
  }

  const stepsToGo = 4 - (steps.stepsCompleted + 1);

  if (
    window.width !== undefined &&
    window.width < MOBILE_BREAKPOINT &&
    stepsToGo > 0
  ) {
    return (
      <>
        <SetUpGuideModal open={showUpgrade} setOpen={setShowUpgrade} />
        <Button
          size="xs"
          onClick={() => setShowUpgrade(!showUpgrade)}
          buttonType="tertiary"
        >
          {stepsToGo === 1 ? "1 step to go" : `${stepsToGo} steps to go`}
        </Button>
      </>
    );
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <SetUpGuideModal open={showUpgrade} setOpen={setShowUpgrade} />
      </ThemeProvider>
      <SetUpGuideStepsWrap
        onClick={() => setShowUpgrade(!showUpgrade)}
        className="setup-guide-link-card"
      >
        <div className="setup-guide-progress-icon">
          <Text
            weight="bold"
            colorPreset="success"
            margin="0"
            size="s"
            isCompact
            align="center"
          >
            {stepsToGo === 1 ? "1 step to go" : `${stepsToGo} steps to go`}
          </Text>
        </div>
        <div className="show-on-nav-hover">
          <Text
            weight="bold"
            colorPreset="success"
            margin="0"
            size="s"
            isCompact
            style={{ whiteSpace: "nowrap" }}
          >
            {stepsToGo === 1 ? "1 step to go" : `${stepsToGo} steps to go`}
          </Text>
          <Flex align="center" margin="0 0 xs">
            <Text
              weight="semi"
              size="xs"
              margin="0"
              style={{ whiteSpace: "nowrap" }}
            >
              Next: {steps.nextStep}
            </Text>
            {steps.nextStep === "Go live" ? (
              <img
                alt="rocket icon"
                width={14}
                height={14}
                src={RocketEmoji}
                style={{ marginLeft: 4, marginBottom: -2 }}
              />
            ) : null}
          </Flex>
          <Button size="xs">View steps</Button>
        </div>
      </SetUpGuideStepsWrap>
    </>
  );
};

const ReconnectSocials = () => {
  const client = useGqlClient();
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const history = useHistory();

  const { data } = useGetBrandSocialsQuery(client, {
    id: activeBrandId ? activeBrandId : "",
  });

  if (
    !data ||
    !data.brand ||
    history.location.pathname.includes("/b/settings")
  ) {
    return null;
  }

  const needsReconnect =
    data.brand.igConnectionExpired || data.brand.ttConnectionExpired;

  if (!needsReconnect) {
    return null;
  }

  return (
    <RouterLink to="/b/settings" style={{ textDecoration: "none" }}>
      <ReconnectSocialsWrap className="reconnect-socials-card">
        <div className="reconnect-alert-icon">
          <WarningIcon width={24} height={24} />
        </div>
        <div className="show-on-nav-hover">
          <Text
            weight="bold"
            colorPreset="warning"
            margin="0"
            size="s"
            isCompact
            style={{ whiteSpace: "nowrap" }}
          >
            {data.brand.igConnectionExpired
              ? "Instagram connection expired"
              : "TikTok connection expired"}
          </Text>
          <Text
            weight="semi"
            size="xs"
            margin="0 0 xs"
            style={{ whiteSpace: "nowrap" }}
          >
            Reconnect to track performance
          </Text>
          <Button size="xs">Reconnect</Button>
        </div>
      </ReconnectSocialsWrap>
    </RouterLink>
  );
};

interface Props {
  steps: Steps;
  expired: boolean;
}

const ProgressBarFg = styled.div<{ expired: boolean; progressWidth: number }>`
  width: ${({ progressWidth }) => `${progressWidth}%`};
  height: 100%;
  background-color: ${(p) =>
    p.expired ? p.theme.color.warning : p.theme.color.primary};
  transition: width 0.3s ease;
  border-radius: ${(p) => p.theme.misc.borderRadius};
`;

const ProgressBarBg = styled.div<{ expired: boolean }>`
  background-color: ${(p) =>
    p.expired ? p.theme.color.warning : p.theme.color.secondary}55;
  border-radius: ${(p) => p.theme.misc.borderRadius};
  width: 100%;
  height: 100%;
`;

const ProgressBarWrap = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  height: 8px;
`;

const LinkSectionText = styled(Text)`
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${(p) => p.theme.color.typography.secondary};
  font-weight: ${(p) => p.theme.typography.weight.bold};
  font-size: ${(p) => p.theme.typography.size.xxs};
  white-space: nowrap;
`;

const BetaTag = styled.span`
  background-color: ${(p) => p.theme.color.pills.greenBackground};
  color: ${(p) => p.theme.color.pills.greenValue};
  padding: 1px 6px 0px;
  border-radius: ${(p) => p.theme.misc.borderRadiusXSmall};
  font-size: ${(p) => p.theme.typography.size.xxxs};
  font-weight: ${(p) => p.theme.typography.weight.bold};
`;

export const ProgressBar: React.FC<Props> = ({ steps, expired }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [progressWidth, setProgressWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) {
        setProgressWidth((steps.currentStep / steps.totalSteps) * 100);
      }
    };

    // Initial width calculation
    updateWidth();

    // Resize observer to handle window resizing
    const handleResize = () => updateWidth();

    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [steps]);

  return (
    <ProgressBarWrap ref={wrapperRef}>
      <ProgressBarBg expired={expired}>
        <ProgressBarFg expired={expired} progressWidth={progressWidth} />
      </ProgressBarBg>
    </ProgressBarWrap>
  );
};
